import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Package, DollarSign, LogOut, Handshake, Plus, List, Pencil, XCircle, Tag, Upload, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { SUPPLIER, MATCH } from '@/constants/testIds';
import { PRODUCT_CATEGORIES, FORMAT_CATEGORIES } from '@/constants/products';
import SettingsButton from '@/components/SettingsButton';
import SubscriptionPaywall from '@/components/SubscriptionPaywall';
import ProductAutocomplete from '@/components/ProductAutocomplete';
import { QRCodeCanvas } from 'qrcode.react';
import QrPickupScanner from '@/components/QrPickupScanner';
import NotificationBell from '@/components/NotificationBell';
import ChatButton, { openChat } from '@/components/ChatButton';
import PeriodFilter, { EMPTY_PERIOD, inPeriod, yearsOf } from '@/components/PeriodFilter';
import GeoFilter, { EMPTY_GEO, inGeo, isGeoActive } from '@/components/GeoFilter';
import LanguageSelector from '@/components/LanguageSelector';
import AdminLink from '@/components/AdminLink';
import SupportButton from '@/components/SupportButton';
import InstallAppButton from '@/components/InstallAppButton';
import DemoBanner from '@/components/DemoBanner';
import DemoTour from '@/components/DemoTour';
import CollapsibleFilters from '@/components/CollapsibleFilters';
import BetaBanner from '@/components/BetaBanner';
import { usePaymentsConfig } from '@/lib/usePaymentsConfig';
import { formatMoney } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { useDeepLink, highlightClass, deliveryLabel } from '@/lib/deepLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierDashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState(() => { const q = new URLSearchParams(window.location.search).get('tab'); return ['subastas','catalogo','publicaciones','matches'].includes(q) ? q : 'subastas'; });
  const highlightId = useDeepLink(setActiveTab, ['subastas', 'catalogo', 'publicaciones', 'matches']);
  const [auctions, setAuctions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showBidModal, setShowBidModal] = useState(false);
  const [showSurplusModal, setShowSurplusModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [bidPrice, setBidPrice] = useState('');
  const [bidDeliveryMode, setBidDeliveryMode] = useState('delivery');
  const [bidDeliveryFee, setBidDeliveryFee] = useState('');
  const [bidVatIncluded, setBidVatIncluded] = useState(false);
  const [bidVatRate, setBidVatRate] = useState('0');
  const [matchData, setMatchData] = useState(null);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [mySurplus, setMySurplus] = useState([]);
  const [editingSurplusId, setEditingSurplusId] = useState(null);
  const [confirmRetireId, setConfirmRetireId] = useState(null);
  const [showPaywallDialog, setShowPaywallDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [matchStatusFilter, setMatchStatusFilter] = useState('pending');
  const [matchPeriod, setMatchPeriod] = useState(EMPTY_PERIOD);
  const [auctionGeo, setAuctionGeo] = useState(EMPTY_GEO);
  const { t } = useTranslation();
  const [surplusPeriod, setSurplusPeriod] = useState(EMPTY_PERIOD);
  const isSurplusExpired = (item) => item.active && new Date(item.expiry_date) < new Date();
  const deleteMany = async (url, ids, okMsg) => {
    const results = await Promise.allSettled(ids.map((id) => axios.delete(`${API}/${url}/${id}`, { headers: { Authorization: getAuthHeader() } })));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    if (ok) toast.success(`${ok} ${okMsg}`);
    if (ok < ids.length) toast.error(`${ids.length - ok} no se pudieron borrar`);
  };
  const [confirmCancelMatchId, setConfirmCancelMatchId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [offerMatchData, setOfferMatchData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [showCatalogForm, setShowCatalogForm] = useState(false);
  const [editingCatalogItem, setEditingCatalogItem] = useState(null);
  const [catalogForm, setCatalogForm] = useState({ product: '', format: '', description: '', category: '', image_url: '' });
  const [catalogDocMeta, setCatalogDocMeta] = useState(null);
  const [catalogDocUploading, setCatalogDocUploading] = useState(false);

  // Compute subscription status from the user object (trial counts as active).
  const payCfg = usePaymentsConfig();
  const isSubscribed = React.useMemo(() => {
    if (!payCfg.payments_enabled) return true;
    if (!user || user.subscription_type === 'none') return false;
    if (!user.subscription_expires_at) return false;
    return new Date(user.subscription_expires_at) > new Date();
  }, [user?.subscription_type, user?.subscription_expires_at, payCfg.payments_enabled]);
  
  const [surplusForm, setSurplusForm] = useState({
    product: '',
    quantity: '',
    format: '',
    original_price: '',
    discount_price: '',
    pricing_mode: 'per_unit',
    expiry_hours: '24',
    delivery_mode: 'either',
    discount_reason: '',
    expiration_from: '',
    expiration_to: '',
    delivery_fee: '',
    price_includes_vat: false,
    vat_rate: '0'
  });

  useEffect(() => {
    if (activeTab === 'subastas') {
      fetchAuctions();
    } else if (activeTab === 'matches') {
      fetchMatches();
    } else if (activeTab === 'publicaciones') {
      fetchMySurplus();
      fetchReceivedOffers();
    } else if (activeTab === 'catalogo') {
      fetchCatalog();
    }
  }, [activeTab]);

  // Re-fetch feed when has_fleet or work_radius change (settings updated)
  useEffect(() => {
    if (activeTab === 'subastas' && user) {
      fetchAuctions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.has_fleet, user?.work_radius_km, user?.postal_code]);

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions/active`, {
        headers: { Authorization: getAuthHeader() }
      });
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    }
  };

  const fetchMySurplus = async () => {
    try {
      const response = await axios.get(`${API}/surplus/my`, {
        headers: { Authorization: getAuthHeader() }
      });
      // Sort: active first, most recent first
      const sorted = response.data.sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setMySurplus(sorted);
    } catch (error) {
      console.error('Error fetching my surplus:', error);
    }
  };

  const [receivedOffersStatus, setReceivedOffersStatus] = useState('pending');
  const [receivedOffersQuery, setReceivedOffersQuery] = useState('');

  const fetchReceivedOffers = async (statusOverride) => {
    try {
      const s = statusOverride ?? receivedOffersStatus;
      const response = await axios.get(`${API}/surplus/offers/received?status=${s}`, {
        headers: { Authorization: getAuthHeader() }
      });
      setReceivedOffers(response.data);
    } catch (error) {
      console.error('Error fetching received offers:', error);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      const res = await axios.post(
        `${API}/surplus/offers/${offerId}/accept`,
        {},
        { headers: { Authorization: getAuthHeader() } }
      );
      toast.success('Oferta aceptada. Match confirmado.');
      setOfferMatchData(res.data);
      fetchReceivedOffers();
      fetchMySurplus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar la oferta');
    }
  };

  const handleRejectOffer = async (offerId) => {
    try {
      await axios.post(
        `${API}/surplus/offers/${offerId}/reject`,
        {},
        { headers: { Authorization: getAuthHeader() } }
      );
      toast.success('Oferta rechazada');
      fetchReceivedOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar la oferta');
    }
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get(`${API}/catalog/my`, { headers: { Authorization: getAuthHeader() } });
      setCatalog(res.data);
    } catch (error) {
      console.error('Error fetching catalog:', error);
    }
    // Also fetch the current catalog document metadata (if any)
    if (user?.id) {
      try {
        const docRes = await axios.get(`${API}/sellers/${user.id}/catalog-document`);
        setCatalogDocMeta({
          filename: docRes.data.filename,
          size_bytes: docRes.data.size_bytes,
          uploaded_at: docRes.data.uploaded_at,
        });
      } catch (_) {
        setCatalogDocMeta(null);
      }
    }
  };

  const openCreateCatalogItem = () => {
    setEditingCatalogItem(null);
    setCatalogForm({ product: '', format: '', description: '', category: '', image_url: '' });
    setShowCatalogForm(true);
  };

  const openEditCatalogItem = (item) => {
    setEditingCatalogItem(item.id);
    setCatalogForm({
      product: item.product,
      format: item.format,
      description: item.description || '',
      category: item.category || '',
      image_url: item.image_url || ''
    });
    setShowCatalogForm(true);
  };

  const handleCatalogImagePick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Selecciona una imagen');
    if (file.size > 5 * 1024 * 1024) return toast.error('La imagen debe pesar menos de 5 MB');
    // Resize to max 600x600 JPEG using canvas
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('read failed'));
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const ratio = Math.min(600 / img.width, 600 / img.height, 1);
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.82));
        };
        img.onerror = () => reject(new Error('image failed'));
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    setCatalogForm(prev => ({ ...prev, image_url: dataUrl }));
  };

  const handleCatalogDocUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') return toast.error('Solo se admiten archivos PDF');
    if (file.size > 9 * 1024 * 1024) return toast.error('El PDF debe pesar menos de 9 MB');
    setCatalogDocUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(new Error('read failed'));
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
      await axios.post(`${API}/sellers/catalog-document`, {
        filename: file.name,
        content_type: file.type,
        data_url: dataUrl,
        size_bytes: file.size,
      }, { headers: { Authorization: getAuthHeader() } });
      toast.success('Catálogo PDF subido');
      setCatalogDocMeta({ filename: file.name, size_bytes: file.size, uploaded_at: new Date().toISOString() });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el PDF');
    } finally {
      setCatalogDocUploading(false);
    }
  };

  const handleCatalogDocDelete = async () => {
    if (!window.confirm('¿Eliminar el catálogo PDF?')) return;
    try {
      await axios.delete(`${API}/sellers/catalog-document`, { headers: { Authorization: getAuthHeader() } });
      setCatalogDocMeta(null);
      toast.success('Catálogo PDF eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveCatalogItem = async () => {
    try {
      const body = {
        product: catalogForm.product,
        format: catalogForm.format,
        description: catalogForm.description || null,
        category: catalogForm.category || null,
        image_url: catalogForm.image_url || null,
      };
      if (editingCatalogItem) {
        await axios.patch(`${API}/catalog/${editingCatalogItem}`, body, { headers: { Authorization: getAuthHeader() } });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/catalog`, body, { headers: { Authorization: getAuthHeader() } });
        toast.success('Producto añadido al catálogo');
      }
      setShowCatalogForm(false);
      setEditingCatalogItem(null);
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el producto');
    }
  };

  const handleDeleteCatalogItem = async (id) => {
    if (!window.confirm('¿Eliminar este producto del catálogo?')) return;
    try {
      await axios.delete(`${API}/catalog/${id}`, { headers: { Authorization: getAuthHeader() } });
      toast.success('Producto eliminado');
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleChangeDelivery = async (matchId, mode) => {
    try {
      await axios.patch(`${API}/matches/${matchId}/delivery`, { delivery_mode: mode }, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success(mode === 'delivery' ? 'Pedido marcado como entrega a domicilio' : 'Pedido marcado como recogida');
      fetchMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar la entrega');
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/my`, {
        headers: { Authorization: getAuthHeader() }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const handleCancelMatch = async (matchId) => {
    try {
      await axios.post(`${API}/matches/${matchId}/cancel`, { reason: cancelReason.trim() || null }, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Pedido cancelado');
      setConfirmCancelMatchId(null);
      setCancelReason('');
      fetchMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cancelar el pedido');
    }
  };

  const handleBid = async () => {
    try {
      await axios.post(
        `${API}/bids`,
        {
          auction_id: selectedAuction.id,
          price: parseFloat(bidPrice),
          delivery_mode: bidDeliveryMode,
          delivery_fee: bidDeliveryFee ? parseFloat(bidDeliveryFee) : 0,
          price_includes_vat: !!bidVatIncluded,
          vat_rate: parseInt(bidVatRate) || 0,
        },
        { headers: { Authorization: getAuthHeader() } }
      );
      toast.success('¡Puja enviada! El restaurante la verá.');
      setShowBidModal(false);
      setBidPrice('');
      setBidDeliveryMode('delivery');
      setSelectedAuction(null);
      fetchAuctions();
    } catch (error) {
      if (error.response?.status === 402) {
        setShowBidModal(false);
        setShowPaywallDialog(true);
        toast.error('Necesitas una suscripción activa para pujar');
      } else {
        toast.error('Error al enviar la puja');
      }
    }
  };

  const handlePublishSurplus = async () => {
    try {
      const payload = {
        quantity: parseInt(surplusForm.quantity),
        format: surplusForm.format,
        original_price: parseFloat(surplusForm.original_price),
        discount_price: parseFloat(surplusForm.discount_price),
        pricing_mode: surplusForm.pricing_mode,
        expiry_hours: parseInt(surplusForm.expiry_hours),
        delivery_mode: surplusForm.delivery_mode,
        discount_reason: surplusForm.discount_reason.trim() || null,
        expiration_from: surplusForm.expiration_from || null,
        expiration_to: surplusForm.expiration_to || null,
        delivery_fee: surplusForm.delivery_fee ? parseFloat(surplusForm.delivery_fee) : 0,
        price_includes_vat: !!surplusForm.price_includes_vat,
        vat_rate: parseInt(surplusForm.vat_rate) || 0,
      };
      if (editingSurplusId) {
        await axios.patch(`${API}/surplus/${editingSurplusId}`, payload, {
          headers: { Authorization: getAuthHeader() }
        });
        toast.success('Publicación actualizada');
      } else {
        await axios.post(`${API}/surplus`, { ...payload, product: surplusForm.product }, {
          headers: { Authorization: getAuthHeader() }
        });
        toast.success('¡Excedente publicado en el mercadillo!');
      }
      setShowSurplusModal(false);
      setEditingSurplusId(null);
      setSurplusForm({
        product: '',
        quantity: '',
        format: '',
        original_price: '',
        discount_price: '',
        pricing_mode: 'per_unit',
        expiry_hours: '24',
        delivery_mode: 'either',
        discount_reason: '',
        expiration_from: '',
        expiration_to: '',
        delivery_fee: '',
        price_includes_vat: false,
        vat_rate: '0'
      });
      if (activeTab === 'publicaciones') fetchMySurplus();
    } catch (error) {
      if (error.response?.status === 402) {
        setShowSurplusModal(false);
        setShowPaywallDialog(true);
        toast.error('Necesitas una suscripción activa para publicar excedentes');
      } else {
        toast.error(error.response?.data?.detail || 'Error al guardar');
      }
    }
  };

  const openEditSurplus = (item) => {
    setEditingSurplusId(item.id);
    // Compute remaining hours from expiry_date
    const remainingMs = new Date(item.expiry_date) - new Date();
    const remainingHours = Math.max(1, Math.round(remainingMs / (1000 * 60 * 60)));
    setSurplusForm({
      product: item.product,
      quantity: String(item.quantity),
      format: item.format,
      original_price: String(item.original_price),
      discount_price: String(item.discount_price),
      pricing_mode: item.pricing_mode || 'per_unit',
      expiry_hours: String(remainingHours),
      delivery_mode: item.delivery_mode || 'either',
      discount_reason: item.discount_reason || '',
      expiration_from: item.expiration_from || '',
      expiration_to: item.expiration_to || '',
      delivery_fee: item.delivery_fee ? String(item.delivery_fee) : '',
      price_includes_vat: !!item.price_includes_vat,
      vat_rate: String(item.vat_rate ?? 0)
    });
    setShowSurplusModal(true);
  };

  const handleRetireSurplus = async (id) => {
    try {
      await axios.post(`${API}/surplus/${id}/close`, {}, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Publicación retirada');
      setConfirmRetireId(null);
      fetchMySurplus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al retirar');
    }
  };

  const urgencyLabels = {
    sos: { label: 'URGENTE', color: 'red' },
    tomorrow: { label: 'Programada', color: 'orange' },
    weekly: { label: 'Peticiones de Oferta', color: 'blue' }
  };

  const frequencyLabels = {
    one_time: 'Puntual',
    weekly: 'Semanal',
    biweekly: 'Quincenal',
    monthly: 'Mensual'
  };

  const deliveryModeLabels = {
    delivery: 'Con transporte',
    pickup: 'Recogida',
    either: 'Flexible'
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={SUPPLIER.dashboard}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-4 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-2xl font-bold text-slate-900 tracking-tight truncate" translate="no" data-testid="header-business-name">
              {user?.business_name}
            </h1>
            <p className="text-xs sm:text-sm text-slate-600">Proveedor</p>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            <Button
              onClick={() => setShowScanner(true)}
              variant="outline"
              size="sm"
              data-testid="open-scanner-button"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 gap-1"
              title="Escanear QR de recogida"
            >
              <QrCode size={16} />
              <span className="hidden sm:inline text-xs">Escanear</span>
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <InstallAppButton variant="icon" />
              <AdminLink />
              <SupportButton />
              <LanguageSelector />
            </div>
            <div className="sm:hidden"><AdminLink /></div>
            <ChatButton />
            <NotificationBell />
            <SettingsButton />
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              data-testid="logout-button"
              className="hidden sm:inline-flex text-slate-600 hover:text-slate-900"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </header>
      <DemoBanner />
      <DemoTour role="seller" activeTab={activeTab} setActiveTab={setActiveTab} steps={[
        { title: t('tour.seller.intro.title'), desc: t('tour.seller.intro.desc'), tip: t('tour.seller.intro.tip') },
        ...['subastas', 'catalogo', 'publicaciones', 'matches'].map((tab) => ({ tab, title: t(`tour.seller.${tab}.title`), desc: t(`tour.seller.${tab}.desc`), tip: tab === 'subastas' ? t('tour.seller.subastas.tip') : undefined })),
      ]} />

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-5 sm:mb-8 h-auto p-1">
            <TabsTrigger value="subastas" data-tour="tab-subastas" className="text-xs sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.auctions')}</TabsTrigger>
            <TabsTrigger value="catalogo" data-tour="tab-catalogo" data-testid="my-catalog-tab" className="text-xs sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.catalog')}</TabsTrigger>
            <TabsTrigger value="publicaciones" data-tour="tab-publicaciones" data-testid="my-publications-tab" className="text-xs sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.surplus')}</TabsTrigger>
            <TabsTrigger value="matches" data-tour="tab-matches" className="text-xs sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.matches')}</TabsTrigger>
          </TabsList>

          {/* Active Auctions Tab */}
          <TabsContent value="subastas" data-testid={SUPPLIER.auctionsFeed}>
            {!payCfg.payments_enabled && <div className="mb-4"><BetaBanner compact /></div>}
            {!isSubscribed && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="subscription-banner">
                <div className="mt-0.5">
                  <span className="text-xl">🔒</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Suscripción necesaria para pujar</p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Puedes ver todas las peticiones gratis, pero necesitas una suscripción activa para pujar. El nombre y contacto del comprador son siempre anónimos hasta que se acepte tu puja. Prueba <strong>1 mes gratis</strong>.
                  </p>
                </div>
                <Button
                  size="sm"
                  data-testid="banner-subscribe-button"
                  onClick={() => setShowPaywallDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                >
                  Suscribirme
                </Button>
              </div>
            )}

            {/* Publish Surplus Button */}
            <Button
              onClick={() => {
                if (!isSubscribed) {
                  setShowPaywallDialog(true);
                } else {
                  setShowSurplusModal(true);
                }
              }}
              data-testid={SUPPLIER.publishSurplusButton}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mb-6 py-6"
            >
              <Plus size={20} className="mr-2" />
              Publicar Excedente en el Mercadillo
            </Button>

            <CollapsibleFilters testId="auction-geo-filters-toggle" active={isGeoActive(auctionGeo)}>
            <div className="flex flex-wrap items-end gap-2" data-testid="auction-geo-filters">
              <GeoFilter testPrefix="auction" value={auctionGeo} onChange={setAuctionGeo} />
              {isGeoActive(auctionGeo) && (
                <Button variant="outline" size="sm" data-testid="auction-geo-clear" onClick={() => setAuctionGeo(EMPTY_GEO)}>{t('common.clear')}</Button>
              )}
            </div>
            </CollapsibleFilters>
            <div className="space-y-4">
              {auctions.filter((a) => inGeo(a, auctionGeo)).length === 0 ? (
                <div className="text-center py-12">
                  <List size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay subastas activas</p>
                </div>
              ) : (
                auctions.filter((a) => inGeo(a, auctionGeo)).map((auction, index) => {
                  const urgencyInfo = urgencyLabels[auction.urgency];
                  const masked = auction.is_masked;
                  return (
                    <div
                      key={auction.id}
                      id={`deeplink-${auction.id}`}
                      className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)}${highlightClass(highlightId, auction.id)}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span
                              className={`bg-${urgencyInfo.color}-100 text-${urgencyInfo.color}-700 text-xs font-bold px-2 py-1 rounded`}
                            >
                              {urgencyInfo.label}
                            </span>
                            <span className="text-xs text-slate-500">{auction.anonymous_name}</span>
                            {auction.distance_km != null && (
                              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-medium">
                                ~{auction.distance_km} km
                              </span>
                            )}
                            {auction.delivery_mode && (
                              <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded font-medium">
                                🚚 {deliveryModeLabels[auction.delivery_mode] || 'Flexible'}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-slate-900">
                            {auction.quantity} {auction.format} de {auction.product}
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {auction.bids_count} puja{auction.bids_count !== 1 ? 's' : ''} enviada{auction.bids_count !== 1 ? 's' : ''}
                          </p>
                          <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                            <p>
                              <span className="font-medium">Cierra:</span>{' '}
                              {new Date(auction.auction_closes_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p>
                              <span className="font-medium">Entrega:</span>{' '}
                              {new Date(auction.delivery_deadline).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {auction.urgency === 'weekly' && auction.frequency && (
                              <p className="text-blue-700 font-medium pt-1">
                                Recurrencia: {frequencyLabels[auction.frequency]}
                                {auction.duration_weeks ? ` durante ${auction.duration_weeks} semanas` : ''}
                              </p>
                            )}
                          </div>
                          {masked && (
                            <p className="text-xs text-amber-700 mt-3 flex items-center gap-1">
                              🔒 Suscríbete para pujar. La identidad del comprador es anónima hasta aceptar tu puja.
                            </p>
                          )}
                        </div>
                      </div>
                      {masked ? (
                        <Button
                          onClick={() => setShowPaywallDialog(true)}
                          data-testid="unlock-bid-button"
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          🔒 Suscribirme para pujar
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setSelectedAuction(auction);
                            // Preselect delivery mode based on restaurant's preference
                            if (auction.delivery_mode === 'pickup') setBidDeliveryMode('pickup');
                            else setBidDeliveryMode('delivery');
                            setShowBidModal(true);
                          }}
                          data-testid={SUPPLIER.bidButton}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Enviar Puja
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Mis Publicaciones Tab */}
          <TabsContent value="catalogo" data-testid="my-catalog-content">
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm text-emerald-900">
                <strong>Catálogo básico gratuito.</strong> Mantén visible tu gama habitual de productos. Los compradores podrán consultarla, pero solo verán tu contacto si tienes suscripción activa.
              </p>
            </div>

            {/* PDF catalog document uploader */}
            <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="catalog-pdf-block">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Catálogo completo en PDF</h3>
              <p className="text-xs text-slate-500 leading-snug mb-3">
                Sube tu catálogo en PDF (máx. 9 MB). Aparecerá como botón de descarga en tu perfil público.
              </p>
              {catalogDocMeta ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs bg-slate-100 text-slate-800 px-2 py-1 rounded truncate max-w-[220px]" data-testid="catalog-pdf-filename">
                    📄 {catalogDocMeta.filename}
                  </span>
                  <span className="text-xs text-slate-500">
                    {(catalogDocMeta.size_bytes / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <label className="text-xs text-emerald-700 hover:underline cursor-pointer">
                    Reemplazar
                    <input type="file" accept="application/pdf" hidden onChange={(e) => handleCatalogDocUpload(e.target.files?.[0])} data-testid="catalog-pdf-replace-input" />
                  </label>
                  <button
                    onClick={handleCatalogDocDelete}
                    data-testid="catalog-pdf-delete-button"
                    className="text-xs text-red-600 hover:underline"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded cursor-pointer" data-testid="catalog-pdf-upload-label">
                  <Upload size={14} /> {catalogDocUploading ? 'Subiendo…' : 'Subir PDF'}
                  <input type="file" accept="application/pdf" hidden onChange={(e) => handleCatalogDocUpload(e.target.files?.[0])} data-testid="catalog-pdf-upload-input" disabled={catalogDocUploading} />
                </label>
              )}
            </div>

            <Button
              onClick={openCreateCatalogItem}
              data-testid="add-catalog-item-button"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mb-6 py-6"
            >
              <Plus size={20} className="mr-2" />
              Añadir producto al catálogo
            </Button>

            {catalog.length === 0 ? (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aún no has añadido productos al catálogo</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="my-catalog-grid">
                {catalog.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`catalog-item-${item.id}`}
                    className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
                  >
                    <div className="aspect-square bg-slate-50 relative">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.product} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                          <Package size={36} />
                        </div>
                      )}
                      {item.category && (
                        <span className="absolute top-1 left-1 text-[10px] bg-white/90 text-slate-700 px-1.5 py-0.5 rounded font-medium">
                          {item.category}
                        </span>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug">{item.product}</p>
                      <p className="text-xs text-slate-500">{item.format}</p>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" variant="ghost" data-testid={`edit-catalog-${item.id}`} onClick={() => openEditCatalogItem(item)} className="text-slate-600 h-7 px-2 text-xs flex-1">
                          <Pencil size={12} className="mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-catalog-${item.id}`} onClick={() => handleDeleteCatalogItem(item.id)} className="text-red-600 h-7 px-2 text-xs">
                          <XCircle size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="publicaciones" data-testid="my-publications-content">
            <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="offers-filters">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-slate-500">Buscar en ofertas</Label>
                <Input
                  data-testid="offers-search"
                  value={receivedOffersQuery}
                  onChange={(e) => setReceivedOffersQuery(e.target.value)}
                  placeholder="Producto o comprador…"
                  className="h-9"
                />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {[
                  { k: 'pending', label: 'Pendientes' },
                  { k: 'accepted', label: 'Aceptadas' },
                  { k: 'rejected', label: 'Rechazadas' },
                  { k: 'expired', label: 'Expiradas' },
                  { k: 'all', label: 'Todas' },
                ].map((opt) => (
                  <Button
                    key={opt.k}
                    size="sm"
                    variant={receivedOffersStatus === opt.k ? 'default' : 'outline'}
                    onClick={() => { setReceivedOffersStatus(opt.k); fetchReceivedOffers(opt.k); }}
                    data-testid={`offers-filter-${opt.k}`}
                    className={receivedOffersStatus === opt.k ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            {(() => {
              const q = receivedOffersQuery.trim().toLowerCase();
              const filteredOffers = q
                ? receivedOffers.filter((o) => `${o.product || ''} ${o.buyer_name || ''}`.toLowerCase().includes(q))
                : receivedOffers;
              return filteredOffers.length > 0 && (
              <div className="mb-6 space-y-3" data-testid="received-offers-section">
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  📩 Ofertas ({filteredOffers.length})
                </h2>
                {filteredOffers.map((offer) => {
                  const total = (offer.quantity * offer.offer_price).toFixed(2);
                  const sellerTotal = (offer.quantity * (offer.discount_price || 0)).toFixed(2);
                  const diffPct = offer.discount_price
                    ? Math.round(((offer.offer_price - offer.discount_price) / offer.discount_price) * 100)
                    : 0;
                  return (
                    <div
                      key={offer.id}
                      data-testid={`offer-card-${offer.id}`}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">
                          NUEVA OFERTA
                        </span>
                        <span className="text-xs text-slate-600">{offer.other_party_name}</span>
                      </div>
                      <p className="text-base font-semibold text-slate-900">
                        {offer.quantity} {offer.format} de {offer.product}
                      </p>
                      {offer.delivery_mode && (
                        <p className="text-xs text-slate-600 mt-1" data-testid={`offer-delivery-${offer.id}`}>
                          🚚 Prefiere: <strong>{offer.delivery_mode === 'delivery' ? 'Entrega a domicilio' : 'Recogida'}</strong>
                        </p>
                      )}
                      <div className="mt-2 text-sm text-slate-700 space-y-0.5">
                        <p>
                          Oferta: <span className="font-bold text-blue-700">€{offer.offer_price.toFixed(2)}</span> / {offer.format}
                          {' · Total '}<span className="font-bold">€{total}</span>
                        </p>
                        {offer.discount_price != null && (
                          <p className="text-xs text-slate-500">
                            Tu precio publicado: €{offer.discount_price.toFixed(2)} / {offer.format} (total €{sellerTotal})
                            {diffPct !== 0 && (
                              <span className={diffPct < 0 ? 'text-red-600 ml-1' : 'text-emerald-600 ml-1'}>
                                {' '}({diffPct > 0 ? '+' : ''}{diffPct}% vs. publicado)
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => handleAcceptOffer(offer.id)}
                          data-testid={`accept-offer-${offer.id}`}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Aceptar
                        </Button>
                        <Button
                          onClick={() => handleRejectOffer(offer.id)}
                          data-testid={`reject-offer-${offer.id}`}
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );})()}

            <Button
              onClick={() => {
                if (!isSubscribed) {
                  setShowPaywallDialog(true);
                } else {
                  setShowSurplusModal(true);
                }
              }}
              data-testid={SUPPLIER.publishSurplusButton}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mb-6 py-6"
            >
              <Plus size={20} className="mr-2" />
              Publicar nuevo excedente
            </Button>

            <div className="space-y-4">
              {(() => {
                const filteredSurplus = mySurplus.filter((it) => inPeriod(it.created_at, surplusPeriod));
                const deletable = filteredSurplus.filter((it) => !it.active || isSurplusExpired(it)).map((it) => it.id);
                return (
                  <>
                    <PeriodFilter
                      testPrefix="my-surplus"
                      value={surplusPeriod}
                      onChange={setSurplusPeriod}
                      years={yearsOf(mySurplus)}
                      deletableCount={deletable.length}
                      deleteLabel="Borrar retiradas/caducadas"
                      onDeleteClosed={async () => { await deleteMany('surplus', deletable, 'publicaciones borradas'); fetchMySurplus(); }}
                    />
              {filteredSurplus.length === 0 ? (
                <div className="text-center py-12">
                  <Tag size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">{mySurplus.length === 0 ? 'Aún no has publicado excedentes' : 'Ninguna publicación en este periodo'}</p>
                </div>
              ) : (
                filteredSurplus.map((item, index) => {
                  const now = new Date();
                  const expiry = new Date(item.expiry_date);
                  const expired = item.active && expiry < now;
                  const discountPct = Math.round(((item.original_price - item.discount_price) / item.original_price) * 100);
                  return (
                    <div
                      key={item.id}
                      data-testid={`my-surplus-card-${item.id}`}
                      className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)} ${!item.active ? 'opacity-70' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">
                              -{discountPct}%
                            </span>
                            {!item.active ? (
                              <span className="bg-slate-200 text-slate-700 text-xs font-medium px-2 py-1 rounded">
                                Retirada
                              </span>
                            ) : expired ? (
                              <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded">
                                Caducada
                              </span>
                            ) : (
                              <span className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-1 rounded">
                                Activa
                              </span>
                            )}
                            {item.delivery_mode && (
                              <span className="bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 rounded">
                                🚚 {deliveryModeLabels[item.delivery_mode] || 'Flexible'}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-slate-900">
                            {item.quantity} {item.format} de {item.product}
                          </h3>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-sm text-slate-400 line-through">
                              €{item.original_price.toFixed(2)}
                            </span>
                            <span className="text-xl font-bold text-emerald-600">
                              €{item.discount_price.toFixed(2)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {item.pricing_mode === 'per_lot' ? 'lote completo' : `/ ${item.format}`}
                            </span>
                          </div>
                          {item.unit_discount_price != null && (
                            <p className="text-xs text-slate-500 mt-1" data-testid={`my-surplus-unit-price-${item.id}`}>
                              {item.pricing_mode === 'per_lot'
                                ? `Equivale a €${item.unit_discount_price.toFixed(2)} / ${item.format}`
                                : `Lote restante: €${(item.unit_discount_price * item.quantity).toFixed(2)}`}
                            </p>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            {item.active && !expired
                              ? `Caduca: ${expiry.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                              : `Publicada: ${new Date(item.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short' })}`}
                          </p>
                        </div>
                      </div>

                      {item.active && !expired && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            onClick={() => openEditSurplus(item)}
                            data-testid="edit-surplus-button"
                            variant="outline"
                            className="flex-1 min-w-[100px]"
                          >
                            <Pencil size={16} className="mr-1" />
                            Editar
                          </Button>
                          <Button
                            onClick={() => setConfirmRetireId(item.id)}
                            data-testid="retire-surplus-button"
                            variant="outline"
                            className="flex-1 min-w-[100px] text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle size={16} className="mr-1" />
                            Retirar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
                  </>
                );
              })()}
            </div>
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            {/* Status filter */}
            <div className="mb-4 flex flex-wrap gap-2" data-testid="match-status-filter">
              {[
                { k: 'pending', label: 'Pendientes' },
                { k: 'closed', label: 'Cerrados' },
                { k: 'all', label: 'Todos' },
              ].map((opt) => (
                <Button
                  key={opt.k}
                  size="sm"
                  variant={matchStatusFilter === opt.k ? 'default' : 'outline'}
                  onClick={() => setMatchStatusFilter(opt.k)}
                  data-testid={`match-filter-${opt.k}`}
                  className={matchStatusFilter === opt.k ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="space-y-4">
              {(() => {
                // A match is "pending" only if it has an active pickup flow
                // (pickup_token) and hasn't been picked up nor cancelled yet.
                // Legacy auction/surplus matches without a pickup_token are
                // considered historical and treated as closed.
                const isPending = (m) => !!m.pickup_token && !m.picked_up && !m.cancelled;
                const isClosed = (m) => !isPending(m);
                const filtered = matches.filter((m) => {
                  if (!inPeriod(m.created_at, matchPeriod)) return false;
                  if (matchStatusFilter === 'pending') return isPending(m);
                  if (matchStatusFilter === 'closed') return isClosed(m);
                  return true;
                });
                const closedIds = filtered.filter(isClosed).map((m) => m.id);
                return (
                  <>
                    <PeriodFilter
                      testPrefix="match"
                      value={matchPeriod}
                      onChange={setMatchPeriod}
                      years={yearsOf(matches)}
                      deletableCount={closedIds.length}
                      onDeleteClosed={async () => { await deleteMany('matches', closedIds, 'pedidos borrados'); fetchMatches(); }}
                    />
                    {filtered.length === 0 ? (
                    <div className="text-center py-12" data-testid="matches-empty">
                      <Handshake size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">
                        {matches.length === 0
                          ? 'No tienes matches aún'
                          : matchStatusFilter === 'pending' ? 'No tienes pedidos pendientes' : 'No tienes pedidos cerrados'}
                      </p>
                    </div>
                    ) : filtered.map((match, index) => {
                  const isCatalog = match.type === 'catalog_order';
                  const isPickedUp = !!match.picked_up;
                  const isCancelled = !!match.cancelled;
                  const canCancel = !isPickedUp && !isCancelled && match.pickup_token;
                  const typeLabel = match.type === 'auction' ? 'Subasta'
                    : match.type === 'catalog_order' ? 'Pedido de catálogo'
                    : 'Excedente';
                  return (
                    <div
                      key={match.id}
                      id={`deeplink-${match.id}`}
                      data-testid={`match-card-${match.id}`}
                      className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)}${highlightClass(highlightId, match.id)}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Handshake size={20} className="text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-600">Match Confirmado</span>
                        </div>
                        {isCancelled ? (
                          <span className="text-xs font-semibold bg-red-100 text-red-800 px-2 py-1 rounded" data-testid="match-status-cancelled">
                            Cancelado{match.cancelled_by === 'buyer' ? ' por el comprador' : match.cancelled_by === 'seller' ? ' por ti' : ''}
                          </span>
                        ) : match.pickup_token ? (
                          isPickedUp ? (
                            <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-1 rounded" data-testid="match-status-picked">
                              ✓ Recogido
                            </span>
                          ) : (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-1 rounded" data-testid="match-status-pending">
                              Pendiente de recoger
                            </span>
                          )
                        ) : null}
                      </div>

                      {isCatalog && match.items && match.items.length > 0 ? (
                        <ul className="text-sm text-slate-900 space-y-0.5 mb-2" data-testid="match-items-list">
                          {match.items.map((it, i) => (
                            <li key={i}><strong>{it.quantity}×</strong> {it.product} <span className="text-slate-500">({it.format})</span></li>
                          ))}
                        </ul>
                      ) : (
                        <h3 className="text-lg font-bold text-slate-900 mb-2">
                          {match.quantity} {match.format} de {match.product}
                        </h3>
                      )}

                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <p className="text-xs text-slate-500 mb-0.5">Comprador</p>
                        <p className="text-sm font-medium text-slate-900">{match.restaurant_name}</p>
                        <p className="text-sm text-slate-600">Tel: {match.restaurant_phone}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`chat-match-${match.id}`}
                          onClick={() => openChat({ buyer_id: match.restaurant_id, context_type: 'match', context_id: match.id })}
                          className="mt-2 h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        >
                          💬 Chat con el comprador
                        </Button>
                      </div>

                      {match.delivery_mode && (
                        <div className={`rounded-lg p-3 mb-3 border text-sm ${match.delivery_mode === 'delivery' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`match-delivery-${match.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">🚚 {deliveryLabel(match.delivery_mode)}</p>
                            {!isPickedUp && !isCancelled && (
                              <Select value={match.delivery_mode === 'either' ? undefined : match.delivery_mode} onValueChange={(v) => handleChangeDelivery(match.id, v)}>
                                <SelectTrigger data-testid={`match-delivery-select-${match.id}`} className="h-8 w-[170px] text-xs bg-white"><SelectValue placeholder="Elegir entrega…" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="delivery">Entrega a domicilio</SelectItem>
                                  <SelectItem value="pickup">Recogida</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          {match.delivery_mode === 'delivery' && match.delivery_fee > 0 && (
                            <p className="text-xs text-slate-600 mt-1">Transporte cobrado: €{Number(match.delivery_fee).toFixed(2)}</p>
                          )}
                        </div>
                      )}

                      {match.pickup_token && !isPickedUp && !isCancelled && (
                        <p className="text-xs text-slate-500 mb-2">
                          Código de recogida: <span className="font-mono font-bold text-slate-900">{match.pickup_token}</span>
                        </p>
                      )}

                      {isPickedUp && match.picked_up_at && (
                        <p className="text-xs text-slate-500 mb-2">
                          Entregado el {new Date(match.picked_up_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}

                      {isCancelled && (
                        <div className="mb-2">
                          {match.cancelled_at && (
                            <p className="text-xs text-slate-500">
                              Cancelado el {new Date(match.cancelled_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          {match.cancelled_reason && (
                            <p className="text-xs text-slate-600 mt-1 italic" data-testid="cancelled-reason">
                              Motivo: "{match.cancelled_reason}"
                            </p>
                          )}
                        </div>
                      )}

                      {match.review && (
                        <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs" data-testid="review-received">
                          <p className="font-semibold text-amber-900">Valoración del comprador: {'★'.repeat(match.review.rating)}<span className="text-slate-300">{'★'.repeat(5 - match.review.rating)}</span></p>
                          {match.review.comment && <p className="text-slate-700 mt-0.5 italic">"{match.review.comment}"</p>}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        {match.price != null ? (
                          <span className="text-slate-600">
                            Precio: €{match.price?.toFixed(2)}{!isCatalog && match.format ? ` / ${match.format}` : ''}
                            {!isCatalog && (match.quantity > 1 || match.delivery_fee > 0) && (
                              <span className="ml-1 font-semibold text-slate-800" data-testid={`match-total-${match.id}`}>· Total €{(match.price * match.quantity + (match.delivery_mode === 'delivery' ? Number(match.delivery_fee || 0) : 0)).toFixed(2)}</span>
                            )}
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                              {(match.vat_rate || 0) > 0 ? `IVA ${match.vat_rate}%${match.price_includes_vat ? ' inc.' : ''}` : 'sin IVA'}
                            </span>
                          </span>
                        ) : <span />}
                        <span className="text-slate-500 text-xs">{typeLabel}</span>
                      </div>
                      {match.delivery_mode === 'delivery' && match.delivery_fee > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          + {formatMoney(match.delivery_fee, match.currency)} de transporte
                        </p>
                      )}
                      {match.reverse_charge && (
                        <p className="text-xs mt-2 px-2 py-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200" data-testid={`match-reverse-charge-${match.id}`}>
                          🇪🇺 {t('common.reverseCharge')} · {match.seller_country} → {match.buyer_country}. {t('common.reverseChargeHint')}
                        </p>
                      )}

                      {canCancel && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmCancelMatchId(match.id)}
                          data-testid={`cancel-match-${match.id}`}
                          className="w-full mt-3 border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Cancelar pedido
                        </Button>
                      )}
                    </div>
                  );
                })}
                  </>
                );
              })()}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bid Modal */}
      <Dialog open={showBidModal} onOpenChange={setShowBidModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Puja</DialogTitle>
            <DialogDescription>
              {selectedAuction?.quantity} {selectedAuction?.format} de {selectedAuction?.product}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tu precio (€) *</Label>
              <Input
                data-testid={SUPPLIER.priceInput}
                type="number"
                step="0.01"
                min="0"
                value={bidPrice}
                onChange={(e) => setBidPrice(e.target.value)}
                placeholder="Ej: 45.50"
              />
            </div>
            <div className="space-y-2">
              <Label>Modo de entrega *</Label>
              <Select value={bidDeliveryMode} onValueChange={setBidDeliveryMode}>
                <SelectTrigger data-testid="bid-delivery-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Con transporte (yo lo llevo)</SelectItem>
                  <SelectItem value="pickup">Recogida (viene a por él)</SelectItem>
                </SelectContent>
              </Select>
              {selectedAuction?.delivery_mode && selectedAuction.delivery_mode !== 'either' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  El restaurante prefiere: <strong>{deliveryModeLabels[selectedAuction.delivery_mode]}</strong>
                </p>
              )}
            </div>

            {bidDeliveryMode === 'delivery' && (
              <div className="space-y-2">
                <Label>Coste de envío (€)</Label>
                <Input
                  data-testid="bid-delivery-fee-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={bidDeliveryFee}
                  onChange={(e) => setBidDeliveryFee(e.target.value)}
                  placeholder="Ej: 5.00"
                />
                <p className="text-[11px] text-slate-400">Se sumará al total del pedido cuando tú entregues.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Tipo de IVA</Label>
              <Select value={bidVatRate} onValueChange={setBidVatRate}>
                <SelectTrigger data-testid="bid-vat-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sin IVA / No aplica</SelectItem>
                  <SelectItem value="4">IVA reducido 4%</SelectItem>
                  <SelectItem value="10">IVA reducido 10%</SelectItem>
                  <SelectItem value="21">IVA general 21%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bidVatRate !== '0' && (
              <div className="flex items-center gap-3 py-1">
                <input
                  type="checkbox"
                  id="bid-vat-included"
                  data-testid="bid-vat-included"
                  className="h-4 w-4 accent-emerald-600"
                  checked={bidVatIncluded}
                  onChange={(e) => setBidVatIncluded(e.target.checked)}
                />
                <Label htmlFor="bid-vat-included" className="cursor-pointer">
                  Mi precio ya lleva el IVA incluido
                </Label>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowBidModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              data-testid={SUPPLIER.submitBidButton}
              onClick={handleBid}
              disabled={!bidPrice || parseFloat(bidPrice) <= 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Enviar Puja
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Surplus Create / Edit Modal */}
      <Dialog open={showSurplusModal} onOpenChange={(open) => {
        setShowSurplusModal(open);
        if (!open) setEditingSurplusId(null);
      }}>
        <DialogContent data-testid={SUPPLIER.surplusForm} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSurplusId ? 'Editar publicación' : 'Publicar Excedente'}</DialogTitle>
            <DialogDescription>
              {editingSurplusId ? 'El producto no se puede cambiar. Puedes ajustar cantidad, precio y duración.' : 'Ofrece tu stock sobrante con descuento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div className="space-y-2">
              <Label>Producto *</Label>
              <ProductAutocomplete
                testId="surplus-product-input"
                value={surplusForm.product}
                onChange={(v) => setSurplusForm({ ...surplusForm, product: v })}
                disabled={!!editingSurplusId}
                placeholder="Ej: Aguacate"
              />
              <p className="text-xs text-slate-500">Escribe libremente; te sugeriremos coincidencias.</p>
            </div>

            <div className="space-y-2">
              <Label>Cantidad *</Label>
              <Input
                type="number"
                min="1"
                value={surplusForm.quantity}
                onChange={(e) => setSurplusForm({ ...surplusForm, quantity: e.target.value })}
                placeholder="Ej: 50"
              />
            </div>

            <div className="space-y-2">
              <Label>Formato / Unidad *</Label>
              <Select
                value={surplusForm.format}
                onValueChange={(value) => setSurplusForm({ ...surplusForm, format: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el formato" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(FORMAT_CATEGORIES).map(([category, items]) => (
                    <SelectGroup key={category}>
                      <SelectLabel className="text-emerald-700 font-semibold">{category}</SelectLabel>
                      {items.map((format) => (
                        <SelectItem key={format} value={format}>
                          {format}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>¿Cómo indicas el precio? *</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="pricing-mode-toggle">
                <button
                  type="button"
                  data-testid="pricing-mode-per-unit"
                  onClick={() => setSurplusForm({ ...surplusForm, pricing_mode: 'per_unit' })}
                  className={`p-3 rounded-lg border-2 text-left transition-colors duration-200 ${
                    surplusForm.pricing_mode === 'per_unit' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <p className="text-xs font-bold text-slate-900">Por {surplusForm.format || 'unidad'}</p>
                  <p className="text-xs text-slate-500 mt-1">Precio de cada {surplusForm.format || 'unidad'}</p>
                </button>
                <button
                  type="button"
                  data-testid="pricing-mode-per-lot"
                  onClick={() => setSurplusForm({ ...surplusForm, pricing_mode: 'per_lot' })}
                  className={`p-3 rounded-lg border-2 text-left transition-colors duration-200 ${
                    surplusForm.pricing_mode === 'per_lot' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <p className="text-xs font-bold text-slate-900">Lote completo</p>
                  <p className="text-xs text-slate-500 mt-1">Precio de {surplusForm.quantity || 'todas'} {surplusForm.format || 'unidades'} juntas</p>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Precio Original (€) * {surplusForm.pricing_mode === 'per_lot' ? '— lote completo' : `— por ${surplusForm.format || 'unidad'}`}</Label>
              <Input
                data-testid="surplus-original-price-input"
                type="number"
                step="0.01"
                min="0"
                value={surplusForm.original_price}
                onChange={(e) => setSurplusForm({ ...surplusForm, original_price: e.target.value })}
                placeholder="Ej: 100.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Precio con Descuento (€) * {surplusForm.pricing_mode === 'per_lot' ? '— lote completo' : `— por ${surplusForm.format || 'unidad'}`}</Label>
              <Input
                data-testid="surplus-discount-price-input"
                type="number"
                step="0.01"
                min="0"
                value={surplusForm.discount_price}
                onChange={(e) => setSurplusForm({ ...surplusForm, discount_price: e.target.value })}
                placeholder="Ej: 50.00"
              />
              {surplusForm.original_price && surplusForm.discount_price && (
                <p className="text-xs text-emerald-600" data-testid="surplus-price-summary">
                  Descuento:{' '}
                  {Math.round(
                    ((parseFloat(surplusForm.original_price) - parseFloat(surplusForm.discount_price)) /
                      parseFloat(surplusForm.original_price)) *
                      100
                  )}
                  %
                  {surplusForm.quantity && parseInt(surplusForm.quantity) > 0 && (
                    surplusForm.pricing_mode === 'per_lot'
                      ? ` · Equivale a €${(parseFloat(surplusForm.discount_price) / parseInt(surplusForm.quantity)).toFixed(2)} / ${surplusForm.format || 'unidad'}`
                      : ` · Lote completo: €${(parseFloat(surplusForm.discount_price) * parseInt(surplusForm.quantity)).toFixed(2)}`
                  )}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Válido durante *</Label>
              <Select
                value={surplusForm.expiry_hours}
                onValueChange={(value) => setSurplusForm({ ...surplusForm, expiry_hours: value })}
              >
                <SelectTrigger data-testid="surplus-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 horas</SelectItem>
                  <SelectItem value="12">12 horas</SelectItem>
                  <SelectItem value="24">24 horas</SelectItem>
                  <SelectItem value="48">2 días</SelectItem>
                  <SelectItem value="72">3 días</SelectItem>
                  <SelectItem value="120">5 días</SelectItem>
                  <SelectItem value="168">1 semana</SelectItem>
                  <SelectItem value="336">2 semanas</SelectItem>
                  <SelectItem value="504">3 semanas</SelectItem>
                  <SelectItem value="720">1 mes</SelectItem>
                  <SelectItem value="1440">2 meses</SelectItem>
                  <SelectItem value="2160">3 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Modo de entrega *</Label>
              <Select
                value={surplusForm.delivery_mode}
                onValueChange={(value) => setSurplusForm({ ...surplusForm, delivery_mode: value })}
              >
                <SelectTrigger data-testid="surplus-delivery-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Con transporte (yo entrego)</SelectItem>
                  <SelectItem value="pickup">Solo recogida</SelectItem>
                  <SelectItem value="either">Flexible (ambos)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(surplusForm.delivery_mode === 'delivery' || surplusForm.delivery_mode === 'either') && (
              <div className="space-y-2">
                <Label>Coste de envío (€)</Label>
                <Input
                  data-testid="surplus-delivery-fee-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={surplusForm.delivery_fee}
                  onChange={(e) => setSurplusForm({ ...surplusForm, delivery_fee: e.target.value })}
                  placeholder="Ej: 5.00"
                />
                <p className="text-[11px] text-slate-400">Se sumará al total del pedido cuando tú entregues.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Tipo de IVA</Label>
              <Select
                value={surplusForm.vat_rate}
                onValueChange={(v) => setSurplusForm({ ...surplusForm, vat_rate: v })}
              >
                <SelectTrigger data-testid="surplus-vat-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sin IVA / No aplica</SelectItem>
                  <SelectItem value="4">IVA reducido 4%</SelectItem>
                  <SelectItem value="10">IVA reducido 10%</SelectItem>
                  <SelectItem value="21">IVA general 21%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {surplusForm.vat_rate !== '0' && (
              <div className="flex items-center gap-3 py-1">
                <input
                  type="checkbox"
                  id="surplus-vat-included"
                  data-testid="surplus-vat-included"
                  className="h-4 w-4 accent-emerald-600"
                  checked={!!surplusForm.price_includes_vat}
                  onChange={(e) => setSurplusForm({ ...surplusForm, price_includes_vat: e.target.checked })}
                />
                <Label htmlFor="surplus-vat-included" className="cursor-pointer">
                  El precio ya lleva el IVA incluido
                </Label>
              </div>
            )}

            <div className="space-y-2">
              <Label>Motivo del descuento</Label>
              <Input
                data-testid="discount-reason-input"
                list="discount-reason-suggestions"
                value={surplusForm.discount_reason}
                onChange={(e) => setSurplusForm({ ...surplusForm, discount_reason: e.target.value })}
                placeholder="Escribe o elige un motivo (opcional)"
                maxLength={140}
              />
              <datalist id="discount-reason-suggestions">
                <option value="Caducidad cercana" />
                <option value="Cambio de envase" />
                <option value="Sobreproducción" />
                <option value="Producto descatalogado" />
                <option value="Cierre temporal" />
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>Caducidad del producto (opcional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">Desde / única</p>
                  <Input
                    data-testid="expiration-from-input"
                    type="date"
                    value={surplusForm.expiration_from}
                    onChange={(e) => setSurplusForm({ ...surplusForm, expiration_from: e.target.value })}
                  />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 mb-1">Hasta (si hay varios lotes)</p>
                  <Input
                    data-testid="expiration-to-input"
                    type="date"
                    value={surplusForm.expiration_to}
                    onChange={(e) => setSurplusForm({ ...surplusForm, expiration_to: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">
                Deja "Hasta" vacío si todos los productos tienen la misma caducidad.
              </p>
            </div>

            <p className="text-xs text-slate-500 bg-slate-50 rounded p-2">
              📍 La dirección y horario de recogida se toman automáticamente de tu perfil.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSurplusModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePublishSurplus}
              disabled={
                !surplusForm.product ||
                !surplusForm.quantity ||
                !surplusForm.format ||
                !surplusForm.original_price ||
                !surplusForm.discount_price
              }
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {editingSurplusId ? 'Guardar cambios' : 'Publicar Excedente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Retire Surplus Dialog */}
      <Dialog open={!!confirmRetireId} onOpenChange={(open) => !open && setConfirmRetireId(null)}>
        <DialogContent data-testid="confirm-retire-dialog">
          <DialogHeader>
            <DialogTitle>¿Retirar publicación?</DialogTitle>
            <DialogDescription>
              La publicación dejará de aparecer en el Mercadillo de Excedentes. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setConfirmRetireId(null)} className="flex-1">
              Cancelar
            </Button>
            <Button
              onClick={() => handleRetireSurplus(confirmRetireId)}
              data-testid="confirm-retire-button"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Sí, retirar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Catalog item form dialog */}
      <Dialog open={showCatalogForm} onOpenChange={(o) => { setShowCatalogForm(o); if (!o) setEditingCatalogItem(null); }}>
        <DialogContent data-testid="catalog-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingCatalogItem ? 'Editar producto' : 'Añadir producto al catálogo'}</DialogTitle>
            <DialogDescription>
              Este catálogo es 100% gratuito para el vendedor. Los compradores podrán ver el producto siempre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Producto *</Label>
              <ProductAutocomplete
                testId="catalog-product-input"
                value={catalogForm.product}
                onChange={(v) => setCatalogForm({ ...catalogForm, product: v })}
                placeholder="Ej: Aceite de oliva virgen extra"
              />
            </div>
            <div className="space-y-2">
              <Label>Formato / Unidad *</Label>
              <Select
                value={catalogForm.format}
                onValueChange={(v) => setCatalogForm({ ...catalogForm, format: v })}
              >
                <SelectTrigger data-testid="catalog-format-select">
                  <SelectValue placeholder="Selecciona el formato" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(FORMAT_CATEGORIES).map(([category, items]) => (
                    <SelectGroup key={category}>
                      <SelectLabel className="text-emerald-700 font-semibold">{category}</SelectLabel>
                      {items.map((format) => (
                        <SelectItem key={format} value={format}>{format}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Input
                data-testid="catalog-category-input"
                value={catalogForm.category}
                onChange={(e) => setCatalogForm({ ...catalogForm, category: e.target.value })}
                placeholder="Ej: Bebidas, Aceites, Lácteos…"
              />
              <p className="text-xs text-slate-500">Los compradores podrán filtrar tu catálogo por categoría.</p>
            </div>
            <div className="space-y-2">
              <Label>Foto del producto</Label>
              <div className="flex items-start gap-3">
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {catalogForm.image_url ? (
                    <img src={catalogForm.image_url} alt="preview" className="w-full h-full object-cover" data-testid="catalog-image-preview" />
                  ) : (
                    <Package size={24} className="text-slate-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <label className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded cursor-pointer w-fit" data-testid="catalog-image-upload-label">
                    <Upload size={14} /> Subir imagen
                    <input type="file" accept="image/*" hidden onChange={(e) => handleCatalogImagePick(e.target.files?.[0])} data-testid="catalog-image-upload-input" />
                  </label>
                  {catalogForm.image_url && (
                    <button
                      type="button"
                      onClick={() => setCatalogForm({ ...catalogForm, image_url: '' })}
                      data-testid="catalog-image-remove"
                      className="text-xs text-red-600 hover:underline text-left"
                    >
                      Quitar imagen
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                data-testid="catalog-description-input"
                value={catalogForm.description}
                onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })}
                placeholder="Ej: Origen Andalucía, cosecha 2025, envasado en garrafa"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowCatalogForm(false)} className="flex-1">Cancelar</Button>
            <Button
              onClick={handleSaveCatalogItem}
              disabled={!catalogForm.product || !catalogForm.format}
              data-testid="catalog-save-button"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {editingCatalogItem ? 'Guardar cambios' : 'Añadir producto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription paywall dialog */}
      <Dialog open={showPaywallDialog} onOpenChange={setShowPaywallDialog}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="paywall-dialog">
          <SubscriptionPaywall onSubscribed={() => { setShowPaywallDialog(false); fetchAuctions(); }} />
        </DialogContent>
      </Dialog>

      {/* Offer accepted → match confirmation dialog for seller */}
      <Dialog open={!!offerMatchData} onOpenChange={(open) => !open && setOfferMatchData(null)}>
        <DialogContent data-testid="offer-match-dialog">
          <DialogHeader>
            <DialogTitle>¡Match confirmado!</DialogTitle>
            <DialogDescription>
              Has aceptado la oferta. Aquí tienes los datos del comprador.
            </DialogDescription>
          </DialogHeader>
          {offerMatchData && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-bold text-slate-900">
                  {offerMatchData.quantity} {offerMatchData.format} de {offerMatchData.product}
                </p>
                <p className="text-sm text-emerald-700 font-semibold mt-1">
                  Precio acordado: €{offerMatchData.price.toFixed(2)} / {offerMatchData.format}
                  {' · Total: '}€{(offerMatchData.quantity * offerMatchData.price + Number(offerMatchData.delivery_fee || 0)).toFixed(2)}
                </p>
                {offerMatchData.delivery_mode && (
                  <p className="text-sm text-slate-700 mt-1" data-testid="offer-match-delivery">
                    🚚 {deliveryLabel(offerMatchData.delivery_mode)}
                    {offerMatchData.delivery_mode === 'delivery' && Number(offerMatchData.delivery_fee || 0) > 0 && ` · transporte €${Number(offerMatchData.delivery_fee).toFixed(2)}`}
                  </p>
                )}
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs text-emerald-800 mb-1">Comprador</p>
                <p className="text-sm font-semibold text-slate-900">{offerMatchData.restaurant_name}</p>
                <p className="text-sm text-slate-700">Tel: {offerMatchData.restaurant_phone}</p>
              </div>
              {offerMatchData.pickup_token && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 mb-1">Código de recogida</p>
                  <p className="text-lg font-mono font-bold text-amber-900">{offerMatchData.pickup_token}</p>
                  {offerMatchData.pickup_address && (
                    <p className="text-xs text-slate-600 mt-1">📍 {offerMatchData.pickup_address}</p>
                  )}
                </div>
              )}
              <Button
                onClick={() => setOfferMatchData(null)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel match confirmation */}
      <Dialog open={!!confirmCancelMatchId} onOpenChange={(open) => { if (!open) { setConfirmCancelMatchId(null); setCancelReason(''); } }}>
        <DialogContent data-testid="confirm-cancel-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Cancelar este pedido?</DialogTitle>
            <DialogDescription>
              El comprador será notificado. Su código de recogida quedará inválido.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <Label className="text-xs text-slate-500">Motivo (opcional)</Label>
            <Input
              data-testid="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              maxLength={280}
              placeholder="Ej: sin stock disponible"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => { setConfirmCancelMatchId(null); setCancelReason(''); }} data-testid="cancel-dialog-abort">
              Volver
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleCancelMatch(confirmCancelMatchId)}
              data-testid="cancel-dialog-confirm"
            >
              Sí, cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Pickup Scanner */}
      <QrPickupScanner open={showScanner} onOpenChange={setShowScanner} />
    </div>
  );
};

export default SupplierDashboard;
